import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/enrollments")({
  component: EnrollmentsAdmin,
});

interface Row {
  id: string;
  child_name: string;
  child_age: number;
  status: string;
  created_at: string;
  courses: { name: string } | null;
  profiles: { full_name: string | null; phone: string | null } | null;
}

const STATUSES = ["pending", "confirmed", "completed", "cancelled"] as const;

function EnrollmentsAdmin() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("enrollments")
      .select("id,child_name,child_age,status,created_at,courses(name),profiles(full_name,phone)")
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows((data ?? []) as unknown as Row[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase
      .from("enrollments")
      .update({ status: status as (typeof STATUSES)[number] })
      .eq("id", id);
    if (error) return toast.error(error.message);
    setRows((arr) => arr.map((r) => (r.id === id ? { ...r, status } : r)));
    toast.success("Updated");
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this enrollment?")) return;
    const { error } = await supabase.from("enrollments").delete().eq("id", id);
    if (error) return toast.error(error.message);
    setRows((arr) => arr.filter((r) => r.id !== id));
  };

  return (
    <div>
      <h1 className="text-3xl font-extrabold mb-6">Enrollments</h1>
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground">مفيش تسجيلات</p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <Card key={r.id} className="rounded-2xl border-0 shadow-soft">
              <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold">
                    {r.courses?.name || "—"} <Badge className="ml-2">{r.status}</Badge>
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Child: {r.child_name} ({r.child_age}y)
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Parent: {r.profiles?.full_name || "—"}
                    {r.profiles?.phone ? ` · ${r.profiles.phone}` : ""}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(r.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="icon" variant="destructive" onClick={() => remove(r.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
